import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { ArrowLeft, Download, Send, MessageSquare, Clock, Building2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Yeni': 'bg-blue-100 text-blue-800',
  'Arandi': 'bg-yellow-100 text-yellow-800',
  'Yonlendirildi': 'bg-green-100 text-green-800',
  'Sonuclandirildi': 'bg-indigo-100 text-indigo-800',
  'Kaybedildi': 'bg-red-100 text-red-800',
};

const STATUSES = ['Yeni', 'Arandi', 'Yonlendirildi', 'Sonuclandirildi', 'Kaybedildi'];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [assignForm, setAssignForm] = useState({ company_id: '', channel: 'telefon', note: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadData = () => {
    Promise.all([
      adminAPI.getLead(id),
      adminAPI.getCompanies()
    ]).then(([leadRes, compRes]) => {
      setData(leadRes.data);
      setCompanies(compRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [id]);

  const handleStatusChange = async (status) => {
    try {
      await adminAPI.updateLeadStatus(id, status);
      toast.success('Durum guncellendi');
      loadData();
    } catch { toast.error('Hata olustu'); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await adminAPI.addLeadNote(id, noteText);
      setNoteText('');
      toast.success('Not eklendi');
      loadData();
    } catch { toast.error('Not eklenemedi'); }
  };

  const handleAssign = async () => {
    if (!assignForm.company_id) { toast.error('Firma secin'); return; }
    setSubmitting(true);
    try {
      await adminAPI.assignLead(id, assignForm);
      setAssignForm({ company_id: '', channel: 'telefon', note: '' });
      toast.success('Lead firmaya yonlendirildi');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Yonlendirme basarisiz'); }
    finally { setSubmitting(false); }
  };

  const handleFileDownload = async (fileId, fileName) => {
    try {
      const response = await adminAPI.downloadFile(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Dosya indirilemedi'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  if (!data) return <p>Lead bulunamadi</p>;

  const { lead, files, assignments } = data;

  return (
    <div data-testid="lead-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/leads')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Geri
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{lead.ad_soyad}</h1>
          <p className="text-sm text-muted-foreground font-mono">{lead.public_code}</p>
        </div>
        <Badge variant="secondary" className={`text-sm ${STATUS_COLORS[lead.status] || ''}`}>{lead.status}</Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-lg">Musteri Bilgileri</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Ad Soyad:</span> <span className="font-medium ml-2">{lead.ad_soyad}</span></div>
                <div><span className="text-muted-foreground">Telefon:</span> <span className="font-medium ml-2">{lead.telefon}</span></div>
                <div><span className="text-muted-foreground">E-posta:</span> <span className="font-medium ml-2">{lead.email || '-'}</span></div>
                <div><span className="text-muted-foreground">Firma:</span> <span className="font-medium ml-2">{lead.sirket_adi || '-'}</span></div>
                <div><span className="text-muted-foreground">Sehir:</span> <span className="font-medium ml-2">{lead.sehir}</span></div>
                <div><span className="text-muted-foreground">Hizmet:</span> <span className="font-medium ml-2">{lead.hizmet}</span></div>
                <div><span className="text-muted-foreground">Adet:</span> <span className="font-medium ml-2">{lead.adet || '-'}</span></div>
                <div><span className="text-muted-foreground">Malzeme:</span> <span className="font-medium ml-2">{lead.malzeme || '-'}</span></div>
                <div><span className="text-muted-foreground">Kalinlik:</span> <span className="font-medium ml-2">{lead.kalinlik || '-'}</span></div>
                <div><span className="text-muted-foreground">Teslim:</span> <span className="font-medium ml-2">{lead.teslim_tarihi || '-'}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-muted-foreground text-sm mb-1">Aciklama:</p>
                <p className="text-sm">{lead.aciklama}</p>
              </div>
              <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
                <p>Kayit: {new Date(lead.created_at).toLocaleString('tr-TR')} | IP: {lead.client_ip}</p>
              </div>
            </CardContent>
          </Card>

          {/* Files */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Dosyalar ({files.length})</CardTitle></CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <p className="text-sm text-muted-foreground">Yuklenen dosya yok</p>
              ) : (
                <div className="space-y-2">
                  {files.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{f.original_name}</p>
                        <p className="text-xs text-muted-foreground">{(f.file_size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleFileDownload(f.id, f.original_name)} data-testid={`download-${f.id}`}>
                        <Download className="h-4 w-4 mr-1" /> Indir
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Notlar</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Not ekle..." data-testid="lead-note-input" />
                <Button onClick={handleAddNote} size="sm" data-testid="lead-note-submit">
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
              {(lead.notes || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Henuz not yok</p>
              ) : (
                <div className="space-y-3">
                  {(lead.notes || []).map(note => (
                    <div key={note.id} className="p-3 bg-slate-50 rounded-lg text-sm">
                      <p>{note.text}</p>
                      <p className="text-xs text-muted-foreground mt-1">{note.author_name || note.author} - {new Date(note.created_at).toLocaleString('tr-TR')}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Actions */}
        <div className="space-y-6">
          {/* Status Change */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Durum Degistir</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-2">
                {STATUSES.map(s => (
                  <Button
                    key={s}
                    variant={lead.status === s ? 'default' : 'outline'}
                    size="sm"
                    className="justify-start"
                    onClick={() => handleStatusChange(s)}
                    data-testid={`status-${s}`}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Assign to Company */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Firmaya Yonlendir</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Firma</Label>
                <select
                  value={assignForm.company_id}
                  onChange={e => setAssignForm(f => ({...f, company_id: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="assign-company-select"
                >
                  <option value="">Firma secin</option>
                  {companies.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Kanal</Label>
                <select
                  value={assignForm.channel}
                  onChange={e => setAssignForm(f => ({...f, channel: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="telefon">Telefon</option>
                  <option value="email">E-posta</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
              <div>
                <Label>Not</Label>
                <Input value={assignForm.note} onChange={e => setAssignForm(f => ({...f, note: e.target.value}))} placeholder="Yonlendirme notu" />
              </div>
              <Button className="w-full" onClick={handleAssign} disabled={submitting} data-testid="admin-lead-assign-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Yonlendir
              </Button>
            </CardContent>
          </Card>

          {/* Assignment History */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Yonlendirme Gecmisi</CardTitle></CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henuz yonlendirme yok</p>
              ) : (
                <div className="space-y-3">
                  {assignments.map(a => (
                    <div key={a.id} className="p-3 border rounded-lg text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{a.company_name}</span>
                      </div>
                      <p className="text-muted-foreground">Kanal: {a.channel}</p>
                      {a.note && <p className="text-muted-foreground">Not: {a.note}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {a.assigned_by_name || a.assigned_by} - {new Date(a.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
